import React, { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../lib/api';
import { quotaVal } from '../lib/tenant';
import { Icons } from '../components/icons';

type FeatureDef = { key: string; label: string; tip?: string };

const FEATURE_GROUPS: { id: string; title: string; tip: string; items: FeatureDef[] }[] = [
  {
    id: 'core',
    title: '核心模块（决定侧栏大菜单）',
    tip: 'MES / LES / QMS / EAM 由套餐 entitlement 闸控；另需租户「生产模式」与角色菜单权限。',
    items: [
      { key: 'mes', label: 'MES 制造执行', tip: '工单、过站、排程等；生产模式须为 MES/混合' },
      { key: 'les', label: 'LES 仓储物流', tip: 'ASN、收货、拣配、电子标签等' },
      { key: 'qms', label: 'QMS 质量', tip: 'IQC/IPQC/FQC、不合格、SPC 等' },
      { key: 'eam', label: 'EAM 设备效率', tip: '资产、点检、维保、OEE' },
    ],
  },
  {
    id: 'sop',
    title: '作业指导',
    tip: 'ESOP 编制与现场执行基础能力',
    items: [
      { key: 'sop_designer', label: 'SOP 设计' },
      { key: 'sop_execute', label: 'SOP 执行' },
      { key: 'andon', label: '安灯' },
      { key: 'vision', label: '视觉检测' },
    ],
  },
  {
    id: 'platform',
    title: '平台增值',
    tip: '协同与集成类能力',
    items: [
      { key: 'integration', label: '外部集成' },
      { key: 'collab', label: '协同编辑' },
      { key: 'version_control', label: '版本管理' },
    ],
  },
];

const ALL_FEATURE_DEFS: FeatureDef[] = FEATURE_GROUPS.flatMap(g => g.items);

function fmtQuota(n: unknown) {
  if (n == null || n === '') return '—';
  if (Number(n) >= 999999) return '不限';
  return Number(n).toLocaleString('zh-CN');
}

function emptyFeatureMap(): Record<string, boolean> {
  const m: Record<string, boolean> = { all: false };
  ALL_FEATURE_DEFS.forEach(d => { m[d.key] = false; });
  return m;
}

function featureChips(features: Record<string, unknown> | undefined) {
  const f = features || {};
  if (f.all) {
    return [{ key: 'all', label: '全部能力', tone: 'accent' as const }];
  }
  const core = FEATURE_GROUPS[0].items.filter(d => f[d.key]).map(d => ({ key: d.key, label: d.label, tone: 'core' as const }));
  const rest = ALL_FEATURE_DEFS.filter(d => !['mes', 'les', 'qms', 'eam'].includes(d.key) && f[d.key])
    .map(d => ({ key: d.key, label: d.label, tone: 'soft' as const }));
  return core.concat(rest);
}

function planFromForm(form: ReturnType<typeof emptyForm>) {
  const features: Record<string, boolean> = {};
  if (form.features.all) {
    features.all = true;
    ALL_FEATURE_DEFS.forEach(d => { features[d.key] = true; });
  } else {
    ALL_FEATURE_DEFS.forEach(d => { if (form.features[d.key]) features[d.key] = true; });
  }
  const quotas: Record<string, number> = {
    max_users: Number(form.quotas.max_users) || 0,
    max_stations: Number(form.quotas.max_stations) || 0,
    max_storage_gb: Number(form.quotas.max_storage_gb) || 0,
  };
  if (form.quotas.max_sops !== '' && form.quotas.max_sops != null) {
    quotas.max_sops = Number(form.quotas.max_sops) || 0;
  }
  return {
    code: form.code.trim(),
    name: form.name.trim(),
    features,
    quotas,
  };
}

function emptyForm() {
  return {
    id: '',
    code: '',
    name: '',
    features: emptyFeatureMap(),
    quotas: { max_users: 5, max_stations: 3, max_storage_gb: 1, max_sops: '' as string | number },
    showJson: false,
    featuresJson: '',
    quotasJson: '',
  };
}

function formFromPlan(p: { id?: string; code?: string; name?: string; features?: Record<string, unknown>; quotas?: Record<string, unknown> }) {
  const f = p.features || {};
  const q = p.quotas || {};
  const features = emptyFeatureMap();
  const allOn = !!f.all;
  features.all = allOn;
  ALL_FEATURE_DEFS.forEach(d => {
    features[d.key] = allOn ? true : !!f[d.key];
  });
  return {
    id: p.id || '',
    code: p.code || '',
    name: p.name || '',
    features,
    quotas: {
      max_users: (q.max_users as number) ?? 5,
      max_stations: (q.max_stations as number) ?? 3,
      max_storage_gb: (q.max_storage_gb as number) ?? 1,
      max_sops: (q.max_sops as string | number) ?? '',
    },
    showJson: false,
    featuresJson: JSON.stringify(f, null, 2),
    quotasJson: JSON.stringify(q, null, 2),
  };
}

function coreModuleCount(features: Record<string, unknown> | undefined) {
  const f = features || {};
  if (f.all) return 4;
  return ['mes', 'les', 'qms', 'eam'].filter(k => f[k]).length;
}

export function Plans() {
  const [list, setList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<'create' | 'edit' | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');

  const load = () => {
    setLoading(true);
    apiFetch('/api/v1/plans').then(d => {
      setList(d.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase();
    if (!kw) return list;
    return list.filter(p =>
      (p.code || '').toLowerCase().includes(kw) || (p.name || '').toLowerCase().includes(kw)
    );
  }, [list, q]);

  const stats = useMemo(() => {
    const tenants = list.reduce((s, p) => s + (Number(p.tenant_count) || 0), 0);
    const full = list.filter(p => p.features && (p.features.all || coreModuleCount(p.features) >= 4)).length;
    return { plans: list.length, tenants, full };
  }, [list]);

  const openCreate = () => { setErr(''); setForm(emptyForm()); setModal('create'); };
  const openEdit = (p: any) => { setErr(''); setForm(formFromPlan(p)); setModal('edit'); };

  const toggleFeature = (key: string) => {
    setForm(prev => {
      const next = { ...prev.features, [key]: !prev.features[key] };
      if (key === 'all') {
        if (next.all) {
          ALL_FEATURE_DEFS.forEach(d => { next[d.key] = true; });
        }
      } else if (!next[key]) {
        next.all = false;
      } else {
        const everyOn = ALL_FEATURE_DEFS.every(d => next[d.key]);
        if (everyOn) next.all = true;
      }
      return { ...prev, features: next };
    });
  };

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      setErr('编码和名称必填');
      return;
    }
    let body;
    if (form.showJson) {
      try {
        body = {
          code: form.code.trim(),
          name: form.name.trim(),
          features: JSON.parse(form.featuresJson || '{}'),
          quotas: JSON.parse(form.quotasJson || '{}'),
        };
      } catch {
        setErr('features / quotas 须为合法 JSON');
        return;
      }
    } else {
      body = planFromForm(form);
      if (!body.features.all && coreModuleCount(body.features) === 0 && !body.features.sop_designer) {
        setErr('请至少勾选一项功能，或直接点「全部能力」');
        return;
      }
    }
    setBusy(true);
    setErr('');
    const d = modal === 'create'
      ? await apiFetch('/api/v1/platform/plans', { method: 'POST', body })
      : await apiFetch('/api/v1/platform/plans/' + form.id, { method: 'PUT', body });
    setBusy(false);
    if (d.code === 0) { setModal(null); load(); }
    else setErr(d.message || '保存失败');
  };

  const remove = async (p: any) => {
    if ((Number(p.tenant_count) || 0) > 0) {
      alert('仍有 ' + p.tenant_count + ' 个租户使用该套餐，无法删除');
      return;
    }
    if (!confirm('确认删除套餐「' + p.name + '」？')) return;
    const d = await apiFetch('/api/v1/platform/plans/' + p.id, { method: 'DELETE' });
    if (d.code === 0) load(); else alert(d.message || '删除失败');
  };

  return (
    <div className="pg">
      <div className="pg-hero">
        <div>
          <div className="pg-kicker">租户与商务</div>
          <h1 className="pg-title">套餐方案</h1>
          <p className="pg-sub">
            配额 + 功能 entitlement。侧栏菜单还受<strong>生产模式</strong>（简易/MES/混合）与<strong>角色菜单权限</strong>约束；
            变更含 MES 的套餐时会自动切到 MES 模式。
          </p>
        </div>
        <div className="pg-hero-actions">
          <button className="btn btn-outline" onClick={load} disabled={loading}><Icons.Refresh />刷新</button>
          <button className="btn btn-primary" onClick={openCreate}><Icons.Plus />新建套餐</button>
        </div>
      </div>

      <div className="plan-kpi-row">
        <div className="plan-kpi"><span>方案数</span><strong>{stats.plans}</strong></div>
        <div className="plan-kpi"><span>已绑定租户</span><strong>{stats.tenants}</strong></div>
        <div className="plan-kpi accent"><span>全模块方案</span><strong>{stats.full}</strong></div>
      </div>

      <div className="plan-callout">
        <Icons.Info w={16} h={16} />
        <div>
          <strong>菜单仍看不到？</strong>
          核对三件事：① 套餐勾了 MES/LES/QMS/EAM；② 租户账户生产模式不是「简易」（全功能套餐会自动切 MES）；③ 角色权限含对应菜单（租户管理员默认全开）。
        </div>
      </div>

      <div className="pg-toolbar">
        <input
          className="form-input pg-search"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="搜索套餐名称或编码…"
        />
        <span className="pg-toolbar-meta">共 {filtered.length} 个套餐</span>
      </div>

      {loading ? (
        <div className="card"><div className="empty"><span className="spinner" /></div></div>
      ) : filtered.length === 0 ? (
        <div className="card">
          <div className="empty">
            <Icons.Empty />
            <div className="empty-text">{list.length === 0 ? '暂无套餐' : '无匹配结果'}</div>
            {list.length === 0 && (
              <button className="btn btn-primary btn-xs" style={{ marginTop: 12 }} onClick={openCreate}>
                <Icons.Plus /> 创建第一个套餐
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="plan-grid">
          {filtered.map(p => {
            const quotas = p.quotas || {};
            const chips = featureChips(p.features);
            const cores = coreModuleCount(p.features);
            return (
              <div key={p.id} className={'plan-card' + (p.features && p.features.all ? ' plan-card-full' : '')}>
                <div className="plan-card-head">
                  <div>
                    <div className="plan-card-name">{p.name}</div>
                    <code className="plan-card-code">{p.code}</code>
                  </div>
                  <div className="plan-card-actions">
                    <button className="btn btn-outline btn-xs" onClick={() => openEdit(p)}>编辑</button>
                    <button className="btn btn-outline btn-xs danger-text" onClick={() => remove(p)}>删除</button>
                  </div>
                </div>
                <div className="plan-card-meta">
                  <span>{Number(p.tenant_count) || 0} 个租户</span>
                  <span>核心模块 {cores}/4</span>
                </div>
                <div className="plan-quota-grid">
                  <div><span>用户</span><strong>{fmtQuota(quotaVal(quotas, 'max_users', null))}</strong></div>
                  <div><span>工位</span><strong>{fmtQuota(quotaVal(quotas, 'max_stations', null))}</strong></div>
                  <div><span>存储</span><strong>{fmtQuota(quotaVal(quotas, 'max_storage_gb', null))}{quotas.max_storage_gb != null && Number(quotas.max_storage_gb) < 999999 ? ' GB' : ''}</strong></div>
                  {quotas.max_sops != null && quotas.max_sops !== '' && (
                    <div><span>SOP</span><strong>{fmtQuota(quotas.max_sops)}</strong></div>
                  )}
                </div>
                <div className="plan-features">
                  {chips.length === 0
                    ? <span className="plan-feature-empty">未配置功能（空套餐=历史兼容全开）</span>
                    : chips.map(c => (
                      <span key={c.key} className={'plan-feature-chip' + (c.tone === 'accent' ? ' accent' : c.tone === 'core' ? ' core' : '')}>
                        {c.label}
                      </span>
                    ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal plan-modal">
            <div className="modal-header">{modal === 'create' ? '新建套餐' : '编辑套餐'}</div>
            <p className="modal-hint">
              勾选「全部能力」会展开并保存 MES/LES/QMS/EAM 等显式开关。租户换到含 MES 的套餐时，若仍是简易模式会自动切到 MES。
            </p>
            <div className="form-row-2">
              <div className="form-group">
                <label>编码</label>
                <input className="form-input" value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} disabled={modal === 'edit'} placeholder="如 starter" />
              </div>
              <div className="form-group">
                <label>名称</label>
                <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="如 基础版" />
              </div>
            </div>

            {!form.showJson ? (
              <>
                <div className="form-group">
                  <label>配额上限</label>
                  <div className="form-row-3">
                    <div>
                      <div className="form-mini-label">用户</div>
                      <input className="form-input" type="number" value={form.quotas.max_users}
                        onChange={e => setForm({ ...form, quotas: { ...form.quotas, max_users: e.target.value } })} />
                    </div>
                    <div>
                      <div className="form-mini-label">工位</div>
                      <input className="form-input" type="number" value={form.quotas.max_stations}
                        onChange={e => setForm({ ...form, quotas: { ...form.quotas, max_stations: e.target.value } })} />
                    </div>
                    <div>
                      <div className="form-mini-label">存储 GB</div>
                      <input className="form-input" type="number" value={form.quotas.max_storage_gb}
                        onChange={e => setForm({ ...form, quotas: { ...form.quotas, max_storage_gb: e.target.value } })} />
                    </div>
                  </div>
                  <div style={{ marginTop: 8, maxWidth: 160 }}>
                    <div className="form-mini-label">SOP（可选）</div>
                    <input className="form-input" type="number" value={form.quotas.max_sops}
                      onChange={e => setForm({ ...form, quotas: { ...form.quotas, max_sops: e.target.value } })} placeholder="留空不限制" />
                  </div>
                  <p className="form-tip">填写 999999 表示不限</p>
                </div>

                <div className="form-group">
                  <label className={'feature-toggle feature-toggle-all' + (form.features.all ? ' on' : '')}>
                    <input type="checkbox" checked={!!form.features.all} onChange={() => toggleFeature('all')} />
                    <span>
                      <strong>全部能力</strong>
                      <em>一键开通目录内全部模块（推荐企业版）</em>
                    </span>
                  </label>
                </div>

                {FEATURE_GROUPS.map(g => (
                  <div key={g.id} className="feature-group">
                    <div className="feature-group-head">
                      <strong>{g.title}</strong>
                      <span>{g.tip}</span>
                    </div>
                    <div className="feature-toggle-grid">
                      {g.items.map(d => (
                        <label key={d.key} className={'feature-toggle' + (form.features[d.key] ? ' on' : '')} title={d.tip || ''}>
                          <input type="checkbox" checked={!!form.features[d.key]} onChange={() => toggleFeature(d.key)} />
                          <span>{d.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <>
                <div className="form-group">
                  <label>功能 (JSON)</label>
                  <textarea className="form-input" rows={6} style={{ fontFamily: 'monospace', fontSize: 12 }}
                    value={form.featuresJson}
                    onChange={e => setForm({ ...form, featuresJson: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>配额 (JSON)</label>
                  <textarea className="form-input" rows={4} style={{ fontFamily: 'monospace', fontSize: 12 }}
                    value={form.quotasJson}
                    onChange={e => setForm({ ...form, quotasJson: e.target.value })} />
                </div>
              </>
            )}

            {err ? <div className="plan-form-error">{err}</div> : null}

            <button
              type="button"
              className="wb-link"
              style={{ marginBottom: 12 }}
              onClick={() => {
                if (!form.showJson) {
                  const body = planFromForm(form);
                  setForm({
                    ...form,
                    showJson: true,
                    featuresJson: JSON.stringify(body.features, null, 2),
                    quotasJson: JSON.stringify(body.quotas, null, 2),
                  });
                } else {
                  try {
                    const features = JSON.parse(form.featuresJson || '{}');
                    const quotas = JSON.parse(form.quotasJson || '{}');
                    setForm(formFromPlan({ id: form.id, code: form.code, name: form.name, features, quotas }));
                  } catch {
                    setErr('JSON 无效，无法切回表单模式');
                  }
                }
              }}
            >
              {form.showJson ? '切回表单编辑' : '高级：JSON 编辑'}
            </button>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal(null)}>取消</button>
              <button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? '保存中…' : '保存'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
